import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import FavoriteButton from 'dpl/favorites/components/FavoriteButton';
import { FOLLOWABLE_TYPES } from 'dpl/favorites/utils/constants';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import {
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import Badge from 'dpl/common/design_system/Badge';
import { capitalize } from 'dpl/shared/utils';

import { usePuppyData } from '../hooks/usePuppyData';
import ActionButton from './ActionButton';
import ShareButton from './PuppyDetails/ShareButton';

const IS_LT_MD = isLessThanBreakpoint('md');

function Actions({ variant }) {
  const { isBuyer, isLoggedIn } = useCurrentUser();
  const { puppy, breeder } = usePuppyData();

  const shouldRenderFavoriteButton = isBuyer || !isLoggedIn;

  if (variant === 'desktop') {
    return (
      <div className="flex justify-between items-center">
        <div className="flex items-center" style={{ gap: 8 }}>
          <UserAvatar user={{ ...breeder, id: breeder.user_id }} size="sm" />
          <span className="lh-normal">Raised by {breeder.program_name}</span>
        </div>
        <div className="flex" style={{ gap: 40 }}>
          <ShareButton
            analytics={{
              view: ANALYTICS_VIEWS.PUPPY_PROFILE,
              view_feature_location:
                ANALYTICS_VIEW_FEATURE_LOCATIONS.PUPPY_PROFILE_HEADER
            }}
          >
            Share
          </ShareButton>
          {shouldRenderFavoriteButton && (
            <FavoriteButton
              isFavorite={puppy.favorited}
              followableType={FOLLOWABLE_TYPES.DOG}
              followableTypeId={puppy.id}
              checkedIconProps={{ label: 'Favorited' }}
              uncheckedIconProps={{ label: 'Favorite' }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-between items-center bb b--black-10 pb4 mb6 mb8-md">
      <ActionButton
        className="fw-medium stone-700"
        iconName="fetch-chevron-left"
        onClick={() => window.history.back()}
      >
        Back
      </ActionButton>
      <div className="flex" style={{ gap: 24 }}>
        <ShareButton aria-label="Share" />
        {shouldRenderFavoriteButton && (
          <FavoriteButton
            isFavorite={puppy.favorited}
            followableType={FOLLOWABLE_TYPES.DOG}
            followableTypeId={puppy.id}
          />
        )}
      </div>
    </div>
  );
}

Actions.propTypes = {
  variant: PropTypes.oneOf(['mobile', 'desktop']).isRequired
};

export default function ActionBar() {
  const { puppy } = usePuppyData();

  const genderBadgeText = useMemo(() => {
    if (!IS_LT_MD || !puppy.reserved) return puppy.gender;

    return capitalize(
      [puppy.reserved && 'Reserved', puppy.gender]
        .filter(Boolean)
        .join(', ')
        .toLowerCase()
    );
  }, [puppy]);

  return (
    <section className="ActionBar container-xxl font-16 pt4">
      {IS_LT_MD && <Actions variant="mobile" />}
      <div className="flex justify-between justify-start-ns items-center">
        <h1 className="heading-3 mb1">{puppy.name}</h1>
        <div>
          {puppy.adult && (
            <Badge className="nowrap ml2 ml4-md" color="gray">
              Adult dog
            </Badge>
          )}
          {genderBadgeText && (
            <Badge
              className="nowrap ml2"
              color={IS_LT_MD && puppy.reserved ? 'rubber' : 'gray'}
            >
              {genderBadgeText}
            </Badge>
          )}
        </div>
      </div>
      {!IS_LT_MD && <Actions variant="desktop" />}
    </section>
  );
}
